import React, { useRef } from 'react'
import classNames from 'classnames'
import useOnScreen from '@/services/hooks/useOnScreen'

const variants = {
  fadeIn: {
    easing: 'ease-out',
    from: 'opacity-0',
    to: 'opacity-100',
  },
  slideUp: {
    easing: 'ease-out',
    from: 'opacity-0 translate-y-full',
    to: 'opacity-100 translate-y-0',
  },
  slideOver: {
    easing: 'ease-out',
    from: 'opacity-0 translate-x-[-10%]',
    to: 'opacity-100 translate-x-0',
  },
}

const speeds = {
  slow: 'duration-[2s]',
  medium: 'duration-1000',
  fast: 'duration-500',
  custom: '', // when passing custom should supply own duration className in className prop
}

interface Variant {
  easing: string
  from: string
  to: string
}

export interface Props {
  as?: React.ElementType
  variant: keyof typeof variants | Variant
  speed?: keyof typeof speeds
  animateOnce?: boolean
  className?: string
  children: React.ReactNode
}

export const AnimateIn: React.FC<Props> = ({
  as: Tag = 'div',
  variant,
  speed = 'medium',
  animateOnce = true,
  children,
  className,
}) => {
  const ref = useRef(null)
  const entered = useOnScreen(ref, { observeOnce: animateOnce })

  const { easing, from, to } =
    typeof variant === 'string' ? variants[variant] : variant
  const duration = speeds[speed]

  return (
    <Tag
      ref={ref}
      className={classNames(
        'transform transition-all',
        easing,
        duration,
        className,
        { [from]: !entered, [to]: entered }
      )}
    >
      {children}
    </Tag>
  )
}

export default AnimateIn
